import React from 'react';
import { Shield, TrendingUp, Users, Brain } from 'lucide-react';
import { Card } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-6">
              Our Mission
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Empower students and families to make informed financial decisions about college education through transparent, data-driven insights.
            </p>
          </div>

          {/* Why This Matters */}
          <Card className="bg-slate-900/50 border-slate-800 p-10 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Why True Cost Matters</h2>
            <div className="space-y-6 text-slate-300 leading-relaxed">
              <p>
                Most families make college decisions based on published sticker prices, prestige rankings, or emotional factors without understanding the true financial impact. This leads to:
              </p>
              <ul className="list-disc list-inside space-y-3 ml-4">
                <li>Overwhelming student debt that takes decades to repay</li>
                <li>Family financial strain that could have been avoided</li>
                <li>Missed opportunities at equally strong but more affordable institutions</li>
                <li>Limited awareness of available scholarships and aid</li>
              </ul>
              <p className="font-semibold text-white">
                We believe financial transparency should come first, not as an afterthought.
              </p>
            </div>
          </Card>

          {/* How We Do It */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">How We Help</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-slate-900/50 border-slate-800 p-8">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">True Cost Calculation</h3>
                <p className="text-slate-400">
                  We calculate your actual out-of-pocket cost after scholarships, grants, and aid—not just the sticker price everyone sees.
                </p>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800 p-8">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Financial Health Scoring</h3>
                <p className="text-slate-400">
                  Our algorithm evaluates whether a college is financially sustainable by measuring cost as a percentage of your family's annual income.
                </p>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800 p-8">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">AI-Assisted Matching</h3>
                <p className="text-slate-400">
                  We use rule-based algorithms to identify colleges with similar academic profiles but lower costs, and match you with verified scholarships.
                </p>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800 p-8">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Real Data Only</h3>
                <p className="text-slate-400">
                  All college costs, acceptance rates, and scholarships are from verified sources. We never hallucinate data or make unsubstantiated estimates.
                </p>
              </Card>
            </div>
          </div>

          {/* AI Methodology */}
          <Card className="bg-slate-900/50 border-slate-800 p-10 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Our AI-Assisted Methodology</h2>
            <div className="space-y-6 text-slate-300 leading-relaxed">
              <p>
                We use AI as a <span className="font-semibold text-white">decision-support tool</span>, not a replacement for real data:
              </p>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-3">What AI Does:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Matches scholarships based on multi-variable eligibility patterns (GPA, income, major, demographic)</li>
                  <li>Identifies comparable colleges using acceptance rates, ranking tiers, and major offerings</li>
                  <li>Optimizes recommendations to surface the most financially viable options</li>
                </ul>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-3">What AI Doesn't Do:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Generate fake scholarship opportunities</li>
                  <li>Estimate costs without verified data sources</li>
                  <li>Replace official financial aid guidance from colleges</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Data Sources */}
          <Card className="bg-slate-900/50 border-slate-800 p-10 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Where Our Data Comes From</h2>
            <div className="space-y-6 text-slate-300 leading-relaxed">
              <p>
                <span className="font-semibold text-white">Transparency is a core value.</span> Here's exactly where our data originates:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    College Cost Data
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Our tuition, housing, meal plan, and fee estimates are based on publicly available information from official college websites, the IPEDS database, and verified third-party sources. <strong className="text-slate-300">These are estimates for planning purposes</strong> and may not reflect current published rates.
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                    Acceptance Rates & Rankings
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Acceptance rates are sourced from Common Data Set reports and official college disclosures. Ranking tiers (Top 10, Top 20, etc.) are general categorizations based on various national ranking publications.
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    Scholarship Information
                  </h3>
                  <p className="text-slate-400 text-sm">
                    All scholarships listed are real programs with links to official application pages. Award amounts and eligibility requirements are sourced from official scholarship websites. <strong className="text-slate-300">Always verify current details before applying.</strong>
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    Financial Health Algorithm
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Our "Financial Health Score" is a proprietary calculation that compares annual college costs to family income. The thresholds (Healthy, Moderate Risk, High Risk) are based on established financial planning guidelines.
                  </p>
                </div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mt-4">
                <p className="text-emerald-300 text-sm">
                  <strong>Data updates:</strong> We regularly update our database to reflect current information. If you notice outdated data, please contact us through our Contact page.
                </p>
              </div>
            </div>
          </Card>

          {/* Disclaimers */}
          <Card className="bg-amber-500/5 border-amber-500/20 p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Important Disclaimers</h2>
            <div className="space-y-3 text-slate-300">
              <p>
                <span className="font-semibold text-amber-400">This tool provides estimates, not financial advice.</span> All calculations are based on the information you provide and publicly available data.
              </p>
              <p>
                <span className="font-semibold text-amber-400">Always verify with official sources.</span> Contact college financial aid offices directly to confirm costs, aid availability, and scholarship details.
              </p>
              <p>
                <span className="font-semibold text-amber-400">Financial situations vary.</span> Our health score thresholds are general guidelines. Your family's unique circumstances may warrant different decisions.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;